// Content type display mapping utility

interface ContentTypeItem {
  id: number;
  code: string;
  sequence: number;
}

// Map actual content type names to UI display names
export const CONTENT_TYPE_DISPLAY_MAPPING: Record<string, string> = {
  "Webinars & Panel Discussions": "Webinars & Panels",
  Infographics: "Infographics & Medical Slides",
  "Medical Slide Library": "Infographics & Medical Slides", // Maps to the same display name
};

// Map UI display names back to actual content type names for API calls
export const CONTENT_TYPE_API_MAPPING: Record<string, string[]> = {
  "Webinars & Panels": ["Webinars & Panel Discussions"],
  "Infographics & Medical Slides": ["Infographics", "Medical Slide Library"],
  Podcasts: ["Podcasts"],
  "Editorials & Research Case Studies": ["Editorials & Research Case Studies"],
  "Expert Opinions": ["Expert Opinions"],
};

// Get display name for a content type
export const getContentTypeDisplayName = (contentTypeName: string): string => {
  return CONTENT_TYPE_DISPLAY_MAPPING[contentTypeName] || contentTypeName;
};

// Get API content type names for a display name
export const getContentTypeApiNames = (displayName: string): string[] => {
  return CONTENT_TYPE_API_MAPPING[displayName] || [displayName];
};

// Group content types by display name
export const groupContentTypesByDisplayName = (
  contentTypes: ContentTypeItem[]
) => {
  const grouped: Record<string, ContentTypeItem[]> = {};

  contentTypes.forEach((type) => {
    const displayName = getContentTypeDisplayName(type.code);
    if (!grouped[displayName]) {
      grouped[displayName] = [];
    }
    grouped[displayName].push(type);
  });

  return grouped;
};

// Get unique display names for content types
export const getUniqueDisplayNames = (
  contentTypes: ContentTypeItem[]
): string[] => {
  const displayNames = contentTypes.map((type) =>
    getContentTypeDisplayName(type.code)
  );
  return Array.from(new Set(displayNames));
};

// Utility function to format values for Solr queries
// Handles both single values and arrays, ensuring proper OR formatting for arrays
export const formatSolrQueryValue = (
  fieldName: string,
  value: number | number[] | string | string[]
): string => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "";
    }
    if (value.length === 1) {
      return `${fieldName}:${value[0]}`;
    }
    return `${fieldName}:(${value.join(" OR ")})`;
  }
  return `${fieldName}:${value}`;
};
